import React, { JSX, ReactElement, forwardRef, memo } from 'react';
import { TD, TH, TR } from '../../components/Table/Table';
import { A, ITextProps, Overline, OverlineSize, P, Span } from '../../components/Texts/Texts';
import styled, { css } from 'styled-components';
import { DisplayCss, FONT_SIZES, fontSizes } from '../../fundamentals/typography/typography';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { Delta, IDeltaProps, PRICE_MOVEMENT, PriceMovement } from '../../components/Delta/Delta';
import { INumericProps, Numeric } from '../../fundamentals/numeric/Numeric';
import { Badge, IBadgeProps } from '../../components/Badge/Badge';
import { Icon, IIconProps } from '../../components/Icon/Icon';
import { IconButton, IIconButtonProps } from '../../components/IconButton/IconButton';
import { BUTTON_MODES, BUTTON_VARIANTS } from '../../components/Button/buttonVariants';
import { MATERIAL_ICONS, MATERIAL_ICONS_TYPES, MaterialIcon } from '../../components/Icon/icons';
import { Button, ICommonButtonProps } from '../../components/Button/Button';
import { Avatar, IAvatarProps } from '../../components/Avatar/Avatar';
import { CountryFlag, ICountryFlagProps } from '../../components/CountryFlag/CountryFlag';
import { DOT_ICONS_VARIANTS, DotIcon } from '../../components/DotIcon/DotIcon';
import { Header } from '@tanstack/react-table';
import { TableSort } from '../../components/Table/TableSort';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { IMiniModalProps, MiniModal } from '../MiniModal/MiniModal';
import { DropdownToggle } from '../Dropdown/DropdownToggle';
import { Dropdown } from '../Dropdown/Dropdown';
import { useEoraCss } from '../../fundamentals/typography/typographyEora';
import { TYPOGRAPHY } from '../../fundamentals/typography/eoraTypography';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { BaseDiv } from '../../common/BaseElements';
import { ITheme } from '../../themes/theme';
import { DateDisplay, IDateDisplayProps } from '../../components/DateDisplay/DateDisplay';
import { ColourFlagSelector } from '../ColourFlagSelector/ColourFlagSelector';

const DEFAULT_SIZE = 'SMALL';

export const OQuoteCellType = {
  Price: 'Price',
  ChgValue: 'ChgValue',
  ChgPercent: 'ChgPercent',
  ChgValueAndPercent: 'ChgValueAndPercent'
} as const;

export type QuoteCellType = (typeof OQuoteCellType)[keyof typeof OQuoteCellType];

export const OCurrencyCellType = {
  Trading: 'Trading',
  Account: 'Account'
} as const;

export type CurrencyCellType = (typeof OCurrencyCellType)[keyof typeof OCurrencyCellType];

const CellInternal = styled(TD)<ITextProps>`
  ${({ textEoraSize }) => useEoraCss(TYPOGRAPHY.PARAGRAPH[textEoraSize ?? DEFAULT_SIZE].REGULAR)}
  ${A} {
    ${({ textEoraSize }) => useEoraCss(TYPOGRAPHY.PARAGRAPH[textEoraSize ?? DEFAULT_SIZE].REGULAR)}
    color: ${({ theme }) => theme.colours.links.colour};
    &:hover {
      color: ${({ theme }) => theme.colours.links.hover};
    }
    &:active {
      color: ${({ theme }) => theme.colours.links.active};
    }
    &:visited {
      color: ${({ theme }) => theme.colours.links.visited};
    }
  }
`;

export type DefaultCellProps = typeof CellInternal.defaultProps;

export const Cell = memo(CellInternal);

const CentredGapContainer = styled(GapContainer)`
  align-items: center;
`;

export type INumericCellProps = DefaultCellProps & {
  numeric: INumericProps;
  footer?: boolean;
  overlineSize?: OverlineSize;
};

export const NumericCell = styled(({ numeric, footer, textAlign = 'right', compact, overlineSize = FONT_SIZES.TEXT_DEFAULT, ...rest }: INumericCellProps) => (
  <Cell compact={compact} textAlign={textAlign} {...rest}>
    {footer ? (
      <Overline size={overlineSize}>
        <Numeric {...numeric} />
      </Overline>
    ) : (
      <Numeric {...numeric} />
    )}
  </Cell>
))``;

export type IDateCellProps = DefaultCellProps & {
  date: IDateDisplayProps;
};

export const DateCell = styled(({ date, compact, ...rest }: IDateCellProps) => (
  <Cell compact={compact} {...rest}>
    <DateDisplay {...date} />
  </Cell>
))``;

export type IDeltaCellProps = DefaultCellProps & {
  delta: Omit<IDeltaProps, 'size'>;
  delta2?: Omit<IDeltaProps, 'size'>;
  cellType?: Omit<QuoteCellType, 'Price'>;
  overlineSize?: OverlineSize;
  footer?: boolean;
};
export const DeltaCell = styled(({ delta, delta2, textAlign = 'right', cellType: cellType, compact, overlineSize, footer, ...rest }: IDeltaCellProps) => (
  <Cell compact={compact} textAlign={textAlign} {...rest}>
    {cellType !== OQuoteCellType.ChgPercent && <Delta deltaSize={SIZES.XX_SMALL} overlineSize={overlineSize} footer={footer} {...delta} />}
    {cellType !== OQuoteCellType.ChgValue && delta2 && (
      <>
        {cellType !== OQuoteCellType.ChgPercent && <br />}
        <Delta deltaSize={SIZES.XX_SMALL} overlineSize={overlineSize} footer={footer} {...delta2} />
      </>
    )}
  </Cell>
))``;

export type IBadgesCellProps = DefaultCellProps & {
  badges: Omit<IBadgeProps, 'size'>[];
};
export const BadgesCell = styled(({ badges, compact, ...rest }: IBadgesCellProps) => (
  <Cell compact={compact} {...rest}>
    <GapContainer gap={8}>
      {badges.map(({ key, ...badge }) => (
        <Badge key={key} size={SIZES.XX_SMALL} {...badge} />
      ))}
    </GapContainer>
  </Cell>
))``;

export type IIconsCellProps = DefaultCellProps & {
  icons: Omit<IIconProps, 'size'>[];
};
export const IconsCell = styled(({ icons, compact, ...rest }: IIconsCellProps) => (
  <Cell compact={compact} {...rest}>
    <GapContainer gap={4}>
      {icons.map(({ key, type = MATERIAL_ICONS_TYPES.OUTLINE, ...icon }) => (
        <Icon key={key} size={SIZES.XX_SMALL} type={type} {...icon} />
      ))}
    </GapContainer>
  </Cell>
))``;

export type AvatarCellSize = typeof SIZES.XX_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM;
export type IAvatarCellProps = typeof CellInternal.defaultProps & {
  avatar: Omit<IAvatarProps, 'size'> & { size?: AvatarCellSize };
};
export const AvatarCell = styled(({ avatar, compact, ...rest }: IAvatarCellProps) => (
  <Cell compact={compact} {...rest}>
    <Avatar {...avatar} />
  </Cell>
))``;

export type IIconButtonsCellProps = DefaultCellProps & {
  iconButtons: Omit<IIconButtonProps, 'size'>[];
};
export const IconButtonsCell = styled(({ iconButtons, compact, ...rest }: IIconButtonsCellProps) => (
  <Cell compact={compact} {...rest}>
    <GapContainer gap={4}>
      {iconButtons.map(({ key, mode = BUTTON_MODES.MINIMAL, variant = BUTTON_VARIANTS.SECONDARY, ...iconButton }) => (
        <IconButton key={key} size={SIZES.XX_SMALL} mode={mode} variant={variant} {...iconButton} />
      ))}
    </GapContainer>
  </Cell>
))``;

export type IButtonsCellProps = DefaultCellProps & {
  buttons: Omit<ICommonButtonProps, 'size'>[];
};
export const ButtonsCell = styled(({ buttons, compact, ...rest }: IButtonsCellProps) => (
  <Cell compact={compact} {...rest}>
    <GapContainer gap={8}>
      {buttons.map(({ key, mode = BUTTON_MODES.BOLD, variant = BUTTON_VARIANTS.SECONDARY, ...button }) => (
        <Button key={key} size={SIZES.X_SMALL} mode={mode} variant={variant} {...button} />
      ))}
    </GapContainer>
  </Cell>
))``;

export type ITrioCellProps = DefaultCellProps & {
  name: string;
  instrument: string;
  unitsElement?: JSX.Element | JSX.Element[];
} & Pick<IAvatarProps, 'img' | 'icon' | 'variant'> &
  Pick<ICountryFlagProps, 'code' | 'alpha2'>;
// TODO improve useActionOnTextCss to work properly for row hover styling
const TrioInstrumentP = styled(P)`
  ${TR}:hover & {
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
`;

export const TrioCell = styled(({ name, instrument, img, icon, variant, code, alpha2, unitsElement, compact, ...rest }: ITrioCellProps) => {
  const { isMobile } = useWindowDimensions();
  return (
    <Cell compact={compact} {...rest}>
      <GapContainer gap={8}>
        <Avatar size={compact || isMobile ? SIZES.XX_SMALL : SIZES.MEDIUM} img={img} icon={icon} variant={variant} instrument={instrument} border />
        <GapContainer gap={false} column>
          <CentredGapContainer gap={8}>
            <TrioInstrumentP textEoraSize='SMALL' textStyle='SEMI_BOLD' gutter={false}>
              {instrument}
            </TrioInstrumentP>
            {(code || alpha2) && !isMobile && <CountryFlag size={SIZES.MICRO} code={code} alpha2={alpha2} border />}
          </CentredGapContainer>
          {compact && !unitsElement
            ? undefined
            : (unitsElement ?? (
                <P textEoraSize='SMALL' textStyle='REGULAR' gutter={false}>
                  {name}
                </P>
              ))}
        </GapContainer>
      </GapContainer>
    </Cell>
  );
})``;

export const QUOTE_STATUS = {
  OPEN: 'Open',
  DELAYED: 'Delayed',
  CLOSED: 'Closed',
  PRE_OPEN_OR_CLOSE: 'PreOpenOrClose',
  EXTENDED_HOURS: 'ExtendedHours',
  INDETERMINATE: 'Indeterminate'
};
type QuoteStatuses = typeof QUOTE_STATUS;
export type QuoteStatus = QuoteStatuses[keyof QuoteStatuses];

const QuoteStatusToDotVariant = {
  [QUOTE_STATUS.OPEN]: DOT_ICONS_VARIANTS.SUCCESS,
  [QUOTE_STATUS.DELAYED]: DOT_ICONS_VARIANTS.WARNING,
  [QUOTE_STATUS.CLOSED]: DOT_ICONS_VARIANTS.SECONDARY,
  [QUOTE_STATUS.PRE_OPEN_OR_CLOSE]: DOT_ICONS_VARIANTS.YELLOW,
  [QUOTE_STATUS.EXTENDED_HOURS]: DOT_ICONS_VARIANTS.INFO
};

export type IQuoteCellProps = DefaultCellProps & {
  lastPrice: number;
  decimalPrecisionForDisplay: number;
  priceDayChange: number;
  percentDayChange: number;
  priceMovement: PriceMovement | number;
  currency: string;
  quoteStatus: QuoteStatus;
  quoteStatusDropdown?: IMiniModalProps;
  lastPriceMovement?: PriceMovement;
  cellType?: QuoteCellType;
};
const CurrencyP = styled(P)`
  color: ${({ theme }) => theme.colours.palette.neutral};
`;
const PriceP = styled(P)``;

const CellGapContainer = styled(GapContainer)<Pick<IQuoteCellProps, 'textAlign'>>`
  align-items: center;
  ${({ textAlign = 'right' }) => css`
    justify-content: ${textAlign};
  `}
`;

const CellFlexGapContainer = styled(GapContainer)<Pick<IQuoteCellProps, 'textAlign'>>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ textAlign = 'flex-end' }) => css`
    align-items: ${textAlign};
  `}
`;

export const QuoteCell = styled(
  ({
    lastPrice,
    decimalPrecisionForDisplay,
    priceDayChange,
    percentDayChange,
    currency,
    quoteStatus,
    quoteStatusDropdown,
    priceMovement,
    textAlign = 'right',
    compact,
    cellType: cellType,
    ...rest
  }: IQuoteCellProps) => (
    <Cell compact={compact} textAlign={textAlign} {...rest}>
      {cellType !== OQuoteCellType.ChgPercent && cellType !== OQuoteCellType.ChgValue && cellType !== OQuoteCellType.ChgValueAndPercent && (
        <CellGapContainer gap={4}>
          {quoteStatusDropdown ? (
            <>
              <Dropdown drop='up' hoverable>
                {quoteStatus !== QUOTE_STATUS.INDETERMINATE && (
                  <DropdownToggle>
                    <DotIcon size={SIZES.XX_MICRO} variant={QuoteStatusToDotVariant[quoteStatus]} />
                  </DropdownToggle>
                )}
                <MiniModal {...quoteStatusDropdown}></MiniModal>
              </Dropdown>
            </>
          ) : // eslint-disable-next-line unicorn/no-nested-ternary
          quoteStatus === QUOTE_STATUS.INDETERMINATE ? (
            <></>
          ) : (
            <DotIcon size={SIZES.XX_MICRO} variant={QuoteStatusToDotVariant[quoteStatus]} />
          )}
          <CurrencyP textEoraSize='MICRO' textStyle='REGULAR' gutter={false}>
            {currency}
          </CurrencyP>
          <PriceP textEoraSize='SMALL' textStyle='SEMI_BOLD' gutter={false}>
            <Numeric value={lastPrice} fractionDigits={decimalPrecisionForDisplay} minimumFractionDigits={decimalPrecisionForDisplay} />
          </PriceP>
        </CellGapContainer>
      )}
      {cellType !== OQuoteCellType.Price && cellType !== OQuoteCellType.ChgValueAndPercent && (
        <Delta
          deltaSize={SIZES.XX_SMALL}
          priceChange={cellType === OQuoteCellType.ChgPercent ? undefined : priceDayChange}
          priceChangePercent={cellType === OQuoteCellType.ChgValue ? undefined : percentDayChange}
          priceChangePercentFractionDigits={2}
          fractionDigits={decimalPrecisionForDisplay}
          priceMovement={priceMovement}
          showIcon
          type='textColoured'
          noWrap
        />
      )}
      {cellType === OQuoteCellType.ChgValueAndPercent && (
        <CellFlexGapContainer gap={4}>
          <Delta
            deltaSize={SIZES.XX_SMALL}
            priceChange={priceDayChange}
            priceChangePercentFractionDigits={2}
            fractionDigits={decimalPrecisionForDisplay}
            priceMovement={priceMovement}
            showIcon
            showPositive
            type='textColoured'
          />
          <Delta
            deltaSize={SIZES.XX_SMALL}
            priceChangePercent={percentDayChange}
            priceChangePercentFractionDigits={2}
            priceChangePercentPrefix='('
            priceChangePercentSuffix=')'
            hidePlusInPercent
            fractionDigits={decimalPrecisionForDisplay}
            type='textColoured'
          />
        </CellFlexGapContainer>
      )}
    </Cell>
  )
)`
  ${({ lastPriceMovement, theme }) => {
    const { colours } = theme as ITheme;
    if (lastPriceMovement === PRICE_MOVEMENT.UP)
      return css`
        background-color: ${colours.eoraCoreColours.BACKGROUND_COLOR.HIGHLIGHTED_CELLS.TABLE_CELL_COLOR_BACKGROUND_HIGHLIGHT_SUCCESS};
      `;
    if (lastPriceMovement === PRICE_MOVEMENT.DOWN)
      return css`
        background-color: ${colours.eoraCoreColours.BACKGROUND_COLOR.HIGHLIGHTED_CELLS.TABLE_CELL_COLOR_BACKGROUND_HIGHLIGHT_ERROR};
      `;
  }}
`;

const DEFAULT_MINIMUM_FRACTION_DIGITS = 2;
const FX_RATE_FRACTION_DIGITS = 3;

export type IAmountCellProps = DefaultCellProps &
  Pick<INumericProps, 'fractionDigits' | 'minimumFractionDigits' | 'noValue' | 'zeroIsNoValue' | 'nanValue'> & {
    amount?: number;
    currency?: string;
    footer?: boolean;
    overlineSize?: OverlineSize;
  };
export const AmountCell = styled(
  ({
    amount,
    currency,
    fractionDigits,
    minimumFractionDigits = DEFAULT_MINIMUM_FRACTION_DIGITS,
    noValue,
    zeroIsNoValue,
    nanValue,
    footer,
    compact,
    overlineSize = FONT_SIZES.TEXT_DEFAULT,
    ...rest
  }: IAmountCellProps) => (
    <NumericCell
      compact={compact}
      numeric={{
        value: amount,
        fractionDigits,
        minimumFractionDigits,
        suffix: currency ? ` ${currency}` : '',
        noValue,
        zeroIsNoValue,
        nanValue
      }}
      footer={footer}
      overlineSize={overlineSize}
      {...rest}
    />
  )
)``;

export type IAmountDeltaCellProps = DefaultCellProps & {
  amount: number;
  minimumFractionDigits?: number;
  footer?: boolean;
  overlineSize?: OverlineSize;
};
export const AmountDeltaCell = styled(
  ({ amount, footer, minimumFractionDigits = DEFAULT_MINIMUM_FRACTION_DIGITS, compact, overlineSize, ...rest }: IAmountDeltaCellProps) => (
    <DeltaCell
      compact={compact}
      delta={{
        priceChange: amount,
        minimumFractionDigits,
        type: 'textColoured'
      }}
      overlineSize={overlineSize}
      footer={footer}
      {...rest}
    />
  )
)``;

export type IMarketValueCellProps = DefaultCellProps & {
  marketValue?: number;
  marketValueCurrency?: string;
  marketValueFx?: number;
  marketValueFxCurrency?: string;
  minimumFractionDigits?: number;
  footer?: boolean;
  cellType?: CurrencyCellType;
  /** Send translated label text for TRADING CURRENCY */
  altCurrencyTypeLabel?: string;
  /** send translated label text for FX RATE */
  altFxRateLabel?: string;
  /** fx rate if market value is in currency different from account currency */
  fxRate?: number;
  overlineSize?: OverlineSize;
};

const ContentLabel = styled(BaseDiv)`
  color: ${({ theme }) => theme.colours.palette.neutral400};
  font-size: ${fontSizes.textNano};
  font-weight: 600;
  text-transform: uppercase;
`;
const ContentNumber = styled(Span)`
  font-size: ${fontSizes.textSmall};
  font-weight: 500;
`;
const ModalContentGroup = styled(BaseDiv)`
  line-height: 1.2;
`;
const ToggleContainer = styled(Span)`
  cursor: pointer;
  font-size: ${fontSizes.textSmall};
  text-align: right;
  text-decoration: none;
`;
const GapContainerRightAligned = styled(GapContainer)`
  justify-content: flex-end;
`;

export const MarketValueCell = styled(
  ({
    marketValue,
    marketValueCurrency,
    marketValueFx,
    marketValueFxCurrency,
    minimumFractionDigits = DEFAULT_MINIMUM_FRACTION_DIGITS,
    footer,
    compact,
    cellType: cellType,
    altCurrencyTypeLabel,
    altFxRateLabel,
    fxRate,
    overlineSize = FONT_SIZES.TEXT_DEFAULT,
    ...rest
  }: IMarketValueCellProps) => {
    if (marketValue === undefined) return <Cell compact={compact} {...rest}></Cell>;

    const cellContent = footer ? (
      <Overline size={overlineSize}>
        {cellType !== OCurrencyCellType.Trading && (
          <Numeric value={marketValueFx} suffix={` ${marketValueFxCurrency}`} minimumFractionDigits={minimumFractionDigits} />
        )}
        {(cellType === OCurrencyCellType.Trading || (!compact && marketValueFxCurrency !== marketValueCurrency)) && (
          <>
            {cellType !== OCurrencyCellType.Trading && <br />}
            <Numeric value={marketValue} suffix={marketValueCurrency ? ` ${marketValueCurrency}` : ``} minimumFractionDigits={minimumFractionDigits} />
          </>
        )}
      </Overline>
    ) : (
      <>
        {cellType !== OCurrencyCellType.Trading && (
          <Numeric value={marketValueFx} suffix={` ${marketValueFxCurrency}`} minimumFractionDigits={minimumFractionDigits} />
        )}
        {(cellType === OCurrencyCellType.Trading || (!compact && marketValueFxCurrency !== marketValueCurrency)) && (
          <>
            {cellType !== OCurrencyCellType.Trading && <br />}
            <Numeric value={marketValue} suffix={marketValueCurrency ? ` ${marketValueCurrency}` : ``} minimumFractionDigits={minimumFractionDigits} />
          </>
        )}
      </>
    );

    if (!compact || marketValueFxCurrency === marketValueCurrency) {
      return (
        <Cell compact={compact} textAlign='right' {...rest}>
          {cellContent}
        </Cell>
      );
    }

    const amount = cellType === OCurrencyCellType.Trading ? marketValueFx : marketValue;
    const currencyLabel = cellType === OCurrencyCellType.Trading ? marketValueFxCurrency : marketValueCurrency;

    return (
      <Cell compact={compact} textAlign='right' {...rest}>
        <GapContainerRightAligned gap={4}>
          <Dropdown drop='up' hoverable>
            <DropdownToggle>
              <ToggleContainer>{cellContent}</ToggleContainer>
            </DropdownToggle>
            <MiniModal noHeader hideClose size='xSmall'>
              <GapContainer column gap={12}>
                <ModalContentGroup>
                  <ContentLabel>{altFxRateLabel}</ContentLabel>
                  <ContentNumber>
                    <Numeric value={fxRate} fractionDigits={FX_RATE_FRACTION_DIGITS} minimumFractionDigits={FX_RATE_FRACTION_DIGITS} />
                  </ContentNumber>
                </ModalContentGroup>
                <ModalContentGroup>
                  <ContentLabel>{altCurrencyTypeLabel}</ContentLabel>
                  <ContentNumber>
                    <Numeric value={amount} suffix={` ${currencyLabel}`} minimumFractionDigits={minimumFractionDigits} />
                  </ContentNumber>
                </ModalContentGroup>
              </GapContainer>
            </MiniModal>
          </Dropdown>
        </GapContainerRightAligned>
      </Cell>
    );
  }
)``;

export type IHeaderCellProps = typeof TH.defaultProps & {
  name?: string;
  iconName?: MaterialIcon;
  header: Header<any, unknown>;
  dropDown?: ReactElement<any>;
  showColourFlag?: boolean;
  onColourToggleClick?: () => void;
  selected?: boolean;
  showChangeIcon?: boolean;
};

export const HeaderCellName = styled(
  forwardRef<HTMLSpanElement, IHeaderCellProps>(({ name, header, iconName, showChangeIcon, ...rest }, ref) => {
    return iconName ? (
      <>
        {name && (
          <Overline small ref={ref} {...rest}>
            {showChangeIcon && (
              <Icon name={MATERIAL_ICONS.Cached} size={SIZES.XXX_SMALL} style={{ marginBottom: '-' + PX_TO_REM[4], marginRight: PX_TO_REM[2] }} />
            )}
            {name}
          </Overline>
        )}
        {iconName && <Icon name={iconName} size={SIZES.XX_SMALL} ref={ref} {...rest} />}
        {header.column.getCanSort() ? <TableSort isSorted={header.column.getIsSorted()} ref={ref} {...rest} /> : <></>}
      </>
    ) : (
      <Overline ref={ref} displayCss={DisplayCss.InlineBlock} small {...rest}>
        {showChangeIcon && <Icon name={MATERIAL_ICONS.Cached} size={SIZES.XXX_SMALL} style={{ marginBottom: '-' + PX_TO_REM[4], marginRight: PX_TO_REM[2] }} />}
        {name} {header.column.getCanSort() ? <TableSort isSorted={header.column.getIsSorted()} /> : <></>}
      </Overline>
    );
  })
)`
  width: 100%;
`;

const ThContainter = styled(TH)<Pick<IHeaderCellProps, 'compact' | 'selected'>>`
  position: relative;
  ${({ compact }) =>
    // use linear-gradient to combine with solid colour & stop scrolling visibilty of table rows behind header
    compact &&
    css`
      &:hover {
        background-image:
          linear-gradient(${({ theme }) => theme.tableColours.th.hover.background}, ${({ theme }) => theme.tableColours.th.hover.background}),
          linear-gradient(
            ${({ theme }) => theme.colours.eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_15},
            ${({ theme }) => theme.colours.eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_15}
          );
      }
    `};
`;

export const HeaderCell: React.FC<IHeaderCellProps> = ({
  name,
  iconName,
  textAlign = 'right',
  header,
  dropDown,
  showChangeIcon,
  showColourFlag,
  onColourToggleClick,
  ...rest
}) => (
  <ThContainter key={header.id} onClick={header.column.getToggleSortingHandler()} noTextDecoration textAlign={textAlign} {...rest}>
    {showColourFlag && <ColourFlagSelector light={true} placement='right-start' triggerOnClickOnly={true} onToggleClick={onColourToggleClick} />}
    {dropDown ?? <HeaderCellName name={name} header={header} iconName={iconName} showChangeIcon={showChangeIcon} />}
  </ThContainter>
);
